import React, { useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { CircularProgress } from '@material-ui/core';

import { accountSelector } from '../../../../account/account-selectors';
import { cancelSubscription } from '../../../../account/actions/cancel-subscription';
import { loadAccount } from '../../../../account/actions/load-account';

import { UiButton } from '../../../../ui/ui-button';
import { UiDatePicker } from '../../../../ui/ui-date-picker';
import { UiDialog } from '../../../../ui/ui-dialog';
import { UiRichTextEditor } from '../../../../ui/ui-rich-text-editor';
import { UiSelect } from '../../../../ui/ui-select';

import { richTextEditorConfig } from '../../../../util/ckeditor-config';
import { formatDate } from '../../../../util/formatters';
import { confirm } from '../../../../widgets/confirmation-dialog';

type InputProps = {
  accountId: number;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: InputProps,
) => ({
  actions: {
    loadAccount: () => dispatch(loadAccount(accountId)),
    cancelSubscription: (cancelReason: string, cancelDetail: string, reachOutDate?: string) =>
      dispatch(cancelSubscription(accountId, cancelReason, cancelDetail, reachOutDate)),
  },
});

const mapStateToProps = (state: any, { accountId }: InputProps) => ({
  account: accountSelector(state, accountId),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const CancelSubscriptionButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ account, actions }: Props) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelDetail, setCancelDetail] = useState<string>('');
  const [reachOutDate, setReachOutDate] = useState<Moment | null>(null);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);

  const toggleDialog = () => setDialogVisible(!dialogVisible);

  const handleCancelButtonClick = () => {
    toggleDialog();
  };

  const handleSubmitCancelSubscriptionClick = async () => {
    if (
      await confirm('Are you sure you would like to cancel your subscription?', {
        cancelButtonLabel: 'No',
      })
    ) {
      setSubmitting(true);
      const reEngageDate = reachOutDate ? reachOutDate.format('MM-DD-YYYY') : undefined;
      const detail = reEngageDate
        ? `${cancelDetail}, Reach Back Out: ${reEngageDate}`
        : cancelDetail;
      await actions.cancelSubscription(cancelReason, detail, reEngageDate);
      setSubmitting(false);
    }
  };

  const handleReasonChange = (value: string) => {
    setCancelReason(value);
  };

  const handleReasonTextChange = (e: any) => setCancelDetail(e.editor.getData());

  const handleDateChange = (date: Moment | null) => {
    setReachOutDate(date);
  };

  // Unknown is the option that exists on Salesforce, but we want to display "Other" for users if they have a reason other than the ones provided.
  // The spacing for "Software Features/Functionality" needs to remain because it needs to align with what we have on Salesforce
  const otherCancelReasonOption = 'Unknown';
  const cancelReasonOptions = [
    'No Longer Hiring',
    'Out of Business',
    'Change of Ownership',
    'Applicant Quality',
    'Applicant Volume',
    'Software Features/Functionality',
    'Budget',
    'Subscription Paused',
    'Failed Payment',
    otherCancelReasonOption,
  ].map((o) => ({
    value: o,
    label:
      o === otherCancelReasonOption
        ? 'Other (Please elaborate)'
        : o === 'Software Features/Functionality'
        ? 'Software Features / Functionality'
        : o,
  }));

  return (
    account && (
      <>
        <UiButton
          className="u-margin-left-md u-margin-bottom-md u-margin-top-xs"
          variant="secondary"
          onClick={handleCancelButtonClick}
        >
          Cancel Subscription
        </UiButton>

        {dialogVisible && (
          <UiDialog title="Cancel Subscription" size="sm">
            <UiDialog.Content>
              <div className="typo-h5">We&apos;re sorry to see you go!</div>
              <div className="u-margin-y-sm">
                {account.nextBaseSubscriptionBillingDate && (
                  <>
                    Your monthly subscription is paid until{' '}
                    {formatDate(account.nextBaseSubscriptionBillingDate, 'MMMM Do, YYYY')}.
                  </>
                )}
                To further improve our services, we would love to hear feedback on why you are
                canceling your subscription.
              </div>
              <div className="typo-subtitle">Cancel Reason</div>
              <UiSelect
                className="u-width-100 u-margin-y-sm"
                options={cancelReasonOptions}
                value={cancelReason}
                onChange={handleReasonChange}
              />
              <div className="typo-subtitle">Comments</div>
              <UiRichTextEditor
                className="u-margin-y-sm"
                data={cancelDetail}
                onChange={handleReasonTextChange}
                config={richTextEditorConfig}
              />
              {cancelReason === 'Subscription Paused' && (
                <>
                  <div>Please select a date to restart your subscription</div>
                  <UiDatePicker
                    className="u-margin-y-sm"
                    defaultDate={moment().add(30, 'd')}
                    maxDate={moment().add(91, 'd')}
                    onChange={handleDateChange}
                    required
                    disablePast
                  />
                </>
              )}
            </UiDialog.Content>
            <UiDialog.Actions>
              <div className="u-spread">
                <UiButton variant="secondary" onClick={toggleDialog} className="u-margin-sm">
                  Never mind
                </UiButton>
                <UiButton
                  variant="primary"
                  onClick={handleSubmitCancelSubscriptionClick}
                  disabled={submitting || !cancelReason || !cancelDetail}
                  className="u-margin-sm"
                >
                  {submitting && <CircularProgress className="u-margin-right-md" size={20} />}{' '}
                  Cancel Subscription
                </UiButton>
              </div>
            </UiDialog.Actions>
          </UiDialog>
        )}
      </>
    )
  );
});
